/**
 * Utility functions for formatting various data types
 */

/**
 * Format large numbers for display in a human-readable format
 * @param {number} value - Number to format
 * @param {boolean} compact - Whether to use compact B/M format (true) or spelled out (false)
 * @returns {string} Formatted number string
 */
export const formatLargeNumber = (value, compact = false) => {
  if (value === null || value === undefined) return "N/A";

  if (value >= 1_000_000_000) {
    const formatted = (value / 1_000_000_000).toFixed(0);
    return compact ? `${formatted}B` : `${formatted} billion`;
  } else if (value >= 1_000_000) {
    const formatted = (value / 1_000_000).toFixed(0);
    return compact ? `${formatted}M` : `${formatted} million`;
  } else if (value >= 1_000) {
    const formatted = (value / 1_000).toFixed(0);
    return compact ? `${formatted}K` : `${formatted} thousand`;
  }

  return value.toString();
};

/**
 * Format a date string to a readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Safely format any numeric value, accounting for different types
 * @param {any} value - Value to format
 * @param {boolean} compact - Whether to use compact format
 * @returns {string} Formatted value as string
 */
export const formatAttributeValue = (value, dataType, compact = false) => {
  if (value === null || value === undefined) return "N/A";

  switch (dataType) {
    case "integer":
      // For large integers, format as millions/billions
      return formatLargeNumber(parseInt(value, 10), compact);

    case "float":
      // Format with 2 decimal places
      return parseFloat(value).toFixed(2);

    case "boolean":
      return value ? "Yes" : "No";

    case "date":
      return formatDate(value);

    case "string":
    default:
      return value.toString();
  }
};

export default {
  formatLargeNumber,
  formatDate,
  formatAttributeValue,
};
